import { useEffect, useState, useRef, useCallback } from "react";
import styles from "../tablesStyle/CategoriesSection.module.css";
import AddCategoryModal from "./ComponentsTable/AddCategoryModal";
import { API_URL } from "../../config/api";

const CategoriesSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState("");
  const wrapperRef = useRef(null);

  // 🔹 Функция авторизованных запросов
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Токен не найден. Авторизуйтесь заново.");

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.error("Ошибка 401: Unauthorized or session expired");
      alert("Сессия истекла. Пожалуйста, войдите снова.");
      localStorage.removeItem("token");
      window.location.reload();
      throw new Error("Unauthorized");
    }

    const data = await response.json().catch(() => ({}));
    return data;
  }, []);

  // 🔹 Получение категорий
  const fetchCategories = useCallback(async () => {
    try {
      const data = await authFetch(`${API_URL}/api/product_categories`);
      if (Array.isArray(data.data)) {
        const formatted = data.data.map((cat) => ({
          id: cat.id,
          name: cat.title,
          parent_id: cat.parent_id,
        }));
        setCategories(formatted);
      } else {
        console.error("Неверный формат категорий:", data);
      }
    } catch (error) {
      console.error("Ошибка при загрузке категорий:", error);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 🔹 Добавление категории
  const handleAddCategory = () => {
    setShowModal(false);
    fetchCategories();
  };

  // 🔹 Выбор категории
  const handleSelect = (id, name) => {
    if (selectedId === id) {
      setSelectedId(null);
      setEditMode(false);
    } else {
      setSelectedId(id);
      setEditMode(false);
      setEditValue(name);
    }
  };

  const handleEdit = () => setEditMode(true);
  const handleCancel = () => setEditMode(false);

  // 🔹 Сохранение изменений
  const handleSave = async () => {
    try {
      await authFetch(`${API_URL}/api/product_categories/${selectedId}`, {
        method: "PUT",
        body: JSON.stringify({ title: editValue }),
      });
      fetchCategories();
      setEditMode(false);
    } catch (error) {
      console.error("Ошибка при обновлении категории:", error);
    }
  };

  // 🔹 Удаление категории
  const handleDelete = async () => {
    try {
      await authFetch(`${API_URL}/api/product_categories/${selectedId}`, {
        method: "DELETE",
      });
      fetchCategories();
      setSelectedId(null);
    } catch (error) {
      console.error("Ошибка при удалении категории:", error);
    }
  };

  // 🔹 Закрытие меню при клике вне блока
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setSelectedId(null);
        setEditMode(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>Категории товаров</h3>

      <div className={styles.card} ref={wrapperRef}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Все категории</h2>
          <button className={styles.addButton} onClick={() => setShowModal(true)}>
            +
          </button>
        </div>

        {categories.length === 0 ? (
          <div className={styles.emptyState}>
            <h3 className={styles.emptyTitle}>Пусто</h3>
            <p className={styles.emptyText}>У Вас еще нет созданных категорий.</p>
          </div>
        ) : (
          <div className={styles.categoryList}>
            {categories.map((cat) => (
              <div key={cat.id}>
                <div
                  className={`${styles.categoryItem} ${
                    selectedId === cat.id ? styles.selected : ""
                  }`}
                  onClick={() => handleSelect(cat.id, cat.name)}
                >
                  <span className={styles.dragIcon}>⋮⋮</span>
                  <div className={styles.categoryContent}>
                    {editMode && selectedId === cat.id ? (
                      <input
                        value={editValue}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                    ) : (
                      <strong>{cat.name}</strong>
                    )}
                    <p>0 подкатегории, 0 товара</p>
                  </div>
                  <span className={styles.arrowIcon}>›</span>
                </div>

                {selectedId === cat.id && (
                  <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                    {editMode ? (
                      <>
                        <button className={styles.save} onClick={handleSave}>
                          Сохранить
                        </button>
                        <button onClick={handleCancel}>Отменить</button>
                      </>
                    ) : (
                      <>
                        <button onClick={handleEdit}>Редактировать</button>
                        <button className={styles.delete} onClick={handleDelete}>
                          Удалить
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <AddCategoryModal onClose={() => setShowModal(false)} onSave={handleAddCategory} />
      )}
    </div>
  );
};

export default CategoriesSection;
